import {PAYMENT_INSTRUCTION_STATUS_GROUP} from '@sollapay/enums';
import {getStartOfWeek, parseMoney, subtractDays} from '@sollapay/utils';

import {UNASSIGNED_BUYER_FILTER_VALUE} from '../config/instructions-filter.config';

import type {InstructionsFilterState} from '../config/instructions-filter.config';
import type {PaymentInstructionStatusGroup} from '@sollapay/enums';
import type {PaymentInstructionDTO} from '@sollapay/types';

export function countInstructionsActiveFilters(filter: InstructionsFilterState): number {
  let count = 0;
  if (filter.datePreset !== 'all') count++;
  if (filter.amountMin != null || filter.amountMax != null) count++;
  if (filter.purchaseIds.length > 0) count++;
  if (filter.buyerPartyIds.length > 0) count++;
  if (filter.paymentMethods.length > 0) count++;
  return count;
}

export function isInstructionsFilterActive(filter: InstructionsFilterState): boolean {
  return countInstructionsActiveFilters(filter) > 0;
}

function resolveDatePresetCutoff(datePreset: InstructionsFilterState['datePreset']): Date | null {
  if (datePreset === 'all') return null;
  if (datePreset === 'this_week') return getStartOfWeek(new Date());
  const days = datePreset === '7d' ? 7 : datePreset === '30d' ? 30 : 90;
  return subtractDays(new Date(), days);
}

/** Applies every filter field; the status-group pill is layered on top via `filterInstructionsByGroup`. */
export function filterInstructions(
  instructions: PaymentInstructionDTO[],
  filter: InstructionsFilterState
): PaymentInstructionDTO[] {
  const dateCutoff = resolveDatePresetCutoff(filter.datePreset);

  return instructions.filter(instruction => {
    if (dateCutoff && new Date(instruction.createdAt) < dateCutoff) return false;

    if (
      filter.purchaseIds.length > 0 &&
      !filter.purchaseIds.includes(instruction.trustBuyerPurchaseId)
    ) {
      return false;
    }

    if (filter.buyerPartyIds.length > 0) {
      const buyerValue = instruction.buyerPartyId ?? UNASSIGNED_BUYER_FILTER_VALUE;
      if (!filter.buyerPartyIds.includes(buyerValue)) return false;
    }

    if (
      filter.paymentMethods.length > 0 &&
      !filter.paymentMethods.includes(instruction.initiationMethod)
    ) {
      return false;
    }

    if (filter.amountMin != null || filter.amountMax != null) {
      const amount = parseMoney(instruction.amount).toNumber();
      if (filter.amountMin != null && amount < filter.amountMin) return false;
      if (filter.amountMax != null && amount > filter.amountMax) return false;
    }

    return true;
  });
}

export function filterInstructionsByGroup(
  instructions: PaymentInstructionDTO[],
  group: PaymentInstructionStatusGroup | 'all'
): PaymentInstructionDTO[] {
  if (group === 'all') return instructions;
  return instructions.filter(i => PAYMENT_INSTRUCTION_STATUS_GROUP[i.status] === group);
}
